/**
 * 保存した検索条件（ユーザーが保存ボタンを押したときだけ保存する）。
 *
 * 保存するのは検索条件だけ。閲覧履歴・検索履歴の自動保存は行わない。
 * 条件は検索結果URLと同じクエリ文字列で保持するので、既存の検索ロジックを
 * そのまま復元に使える（ページ全体の再読み込みは不要）。
 */
import { describeFilters, filtersToParams, paramsToFilters, parseSort } from "@/lib/search";
import { sortLabels } from "@/lib/labels";
import { STORAGE_KEYS, safeReadString, safeWriteString } from "@/lib/storage";
import type { AudienceType, SortKey, WorkFilters } from "@/types/work";

export const SAVED_SEARCH_LIMIT = 50;
export const SAVED_SEARCH_NAME_MAX = 60;
export const SAVED_SEARCH_PARAMS_MAX = 2000;

export const SAVED_SEARCH_STORAGE_KEY = STORAGE_KEYS.savedSearches;

export interface SavedSearch {
  id: string;
  name: string;
  createdAt: string;
  /** 保存時の表示モード */
  mode: AudienceType;
  /** 検索条件（sort を除くクエリ文字列） */
  params: string;
  sort: SortKey;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function normalizeSavedSearchName(raw: unknown): string {
  if (typeof raw !== "string") return "";
  const withoutControl = Array.from(raw)
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code >= 32 && code !== 127;
    })
    .join("");
  const trimmed = withoutControl.replace(/[\s\u3000]+/g, " ").trim();
  return trimmed.slice(0, SAVED_SEARCH_NAME_MAX);
}

export function createSavedSearchId(): string {
  const random = Math.random().toString(36).slice(2, 8);
  return `ss-${Date.now().toString(36)}-${random}`;
}

/** 現在の検索条件から保存用データを作る（保存はまだしない） */
export function buildSavedSearch(
  name: string,
  filters: WorkFilters,
  sort: SortKey,
  mode: AudienceType,
): SavedSearch {
  return {
    id: createSavedSearchId(),
    name: normalizeSavedSearchName(name),
    createdAt: new Date().toISOString(),
    mode,
    params: filtersToParams(filters, sort, 1).toString(),
    sort,
  };
}

/** 保存した条件を検索画面で使える形に戻す */
export function restoreSavedSearch(saved: SavedSearch): { filters: WorkFilters; sort: SortKey } {
  const params = new URLSearchParams(saved.params);
  return {
    filters: paramsToFilters(params, saved.mode),
    sort: saved.sort,
  };
}

export function savedSearchSummary(saved: SavedSearch): string {
  const { filters } = restoreSavedSearch(saved);
  const condition = describeFilters(filters);
  return `${saved.mode === "female" ? "女性向け" : "男性向け"}・${sortLabels[saved.sort]}・${condition}`;
}

function parseOne(raw: unknown, seenIds: Set<string>): SavedSearch | null {
  if (!isRecord(raw)) return null;
  const id = typeof raw.id === "string" ? raw.id.trim() : "";
  if (!id || id.length > 64 || seenIds.has(id)) return null;
  const name = normalizeSavedSearchName(raw.name);
  if (!name) return null;
  // 条件なし（すべての作品）も保存できるため、空のクエリ文字列を許可する
  const params = typeof raw.params === "string" ? raw.params.trim() : null;
  if (params === null || params.length > SAVED_SEARCH_PARAMS_MAX) return null;
  const mode: AudienceType = raw.mode === "female" ? "female" : "male";
  const sort = parseSort(typeof raw.sort === "string" ? raw.sort : null);

  try {
    // クエリ文字列として解釈できるものだけを受け入れる
    new URLSearchParams(params);
  } catch {
    return null;
  }

  seenIds.add(id);
  return {
    id,
    name,
    createdAt: typeof raw.createdAt === "string" ? raw.createdAt : "",
    mode,
    params,
    sort,
  };
}

/** 保存データを検証して読み込む。壊れた項目だけを捨てる。 */
export function parseSavedSearchList(value: unknown): SavedSearch[] {
  if (!Array.isArray(value)) return [];
  const seenIds = new Set<string>();
  const result: SavedSearch[] = [];
  value.forEach((entry) => {
    if (result.length >= SAVED_SEARCH_LIMIT) return;
    const parsed = parseOne(entry, seenIds);
    if (parsed) result.push(parsed);
  });
  return result;
}

export function readSavedSearches(): SavedSearch[] {
  const raw = safeReadString(SAVED_SEARCH_STORAGE_KEY);
  if (!raw) return [];
  try {
    return parseSavedSearchList(JSON.parse(raw));
  } catch {
    return [];
  }
}

export function serializeSavedSearches(list: SavedSearch[]): string {
  return JSON.stringify(parseSavedSearchList(list));
}

export function writeSavedSearches(list: SavedSearch[]): boolean {
  return safeWriteString(SAVED_SEARCH_STORAGE_KEY, serializeSavedSearches(list));
}

/** 同じ名前の保存条件があるか（保存前の案内用） */
export function hasSameName(list: SavedSearch[], name: string, excludeId?: string): boolean {
  const normalized = normalizeSavedSearchName(name).toLowerCase();
  if (!normalized) return false;
  return list.some((item) => item.id !== excludeId && item.name.toLowerCase() === normalized);
}